import type { Action, BlockNode, ExperienceDocument, Viewport } from '@personalizehq/schemas/ir';
import { mount, type LayoutSnapshot, type MountHandle } from '@personalizehq/renderer-web';
import { useEffect, useMemo, useRef, useState } from 'react';

// The canvas. It is the PRODUCTION renderer — not a preview approximation — so what the author
// sees is what the SDK will draw, down to the wrap points.
//
// Three things here are load-bearing and easy to "simplify" back into bugs:
//
// 1. `contain: paint` on the frame. The renderer's overlay host is `position: fixed; inset: 0` —
//    correct in a customer's app, catastrophic here, where it would cover the whole dashboard.
//    `contain` makes this div the containing block for that fixed child, so the overlay resolves to
//    exactly the artboard's box. (Over `transform`: same containing-block effect, plus clipping,
//    without transform's text-rasterisation side effects. A `transform: scale()` for zoom in E4
//    then composes with it.)
// 2. `onDismiss` is a no-op. Esc, a scrim click and any `{type:'dismiss'}` button all funnel
//    through the renderer's one dismiss closure, whose default is `destroy()`. An author pressing
//    Esc would otherwise delete their own canvas.
// 3. Every viewport field is PINNED. The renderer defaults `font_scale` from the DASHBOARD's root
//    font size and `direction` from the DASHBOARD's `dir` — leak either and the artboard silently
//    stops showing what the device will.

/** The solver emits nodes the DOCUMENT does not contain: `repeat` expansion writes `${id}__${i}`
 *  and `progress` writes `${id}_seg${i}` (measure.ts). `nodeAt()` hands those back, so selecting
 *  one would select a node that does not exist. Map back to the authored node instead. */
export function toDocId(id: string): string {
  return id.replace(/__\d+$/, '').replace(/_seg\d+$/, '');
}

/** THE EMPTY-CONTAINER PROBLEM. An empty `stack`/`box`/`scroll`/`grid` measures to nothing — hug
 *  content with no content is zero — so a container the author just inserted is INVISIBLE and, worse,
 *  un-hittable: `nodeAt()` can never return a node whose frame has no area. Without this the palette
 *  appears broken ("I clicked stack and nothing happened"), and E2's drag-insert would have nowhere
 *  to drop.
 *
 *  The placeholder is drawn in the EDITOR'S OVERLAY, never in `renderer-web` — the renderer must keep
 *  rendering exactly what the device renders, or the canvas stops being pixel-honest, which is the
 *  whole reason it runs the production renderer. The overlay is also where its click target lives. */
const EMPTY_MIN_W = 64;
const EMPTY_MIN_H = 40;

function emptyContainers(root: BlockNode): Map<string, string> {
  const found = new Map<string, string>();
  const walk = (node: BlockNode): void => {
    if ('children' in node) {
      if (node.children.length === 0) found.set(node.id, node.block);
      else node.children.forEach(walk);
    }
  };
  walk(root);
  return found;
}

interface ArtboardProps {
  doc: ExperienceDocument;
  viewport: Viewport;
  selection: string | null;
  onSelect: (id: string | null) => void;
  /** asset id → URL. Fonts are BUNDLED (SPEC §4.1): a font token's `src` is an asset id, and
   *  without this map the renderer measures with a fallback family — every wrap point moves. */
  assets?: Record<string, string> | undefined;
  /** Wireframe every node's rect. The rects are the solver's, so this costs one loop. */
  outlines?: boolean;
  /** INTERACTIVE PREVIEW (B4). The renderer has always taken an `onAction`; the artboard just never
   *  passed one, so every button on the canvas decoded its actions and fired nothing. With it, a
   *  `flow_next` walks the steps and the author can actually TRY the flow they authored. */
  onAction?: ((action: Action, nodeId: string) => void) | undefined;
}

export function Artboard({
  doc,
  viewport,
  selection,
  onSelect,
  onAction,
  assets,
  outlines,
}: ArtboardProps): React.JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<MountHandle | null>(null);
  const [nodes, setNodes] = useState<LayoutSnapshot['nodes']>([]);
  const [hover, setHover] = useState<string | null>(null);
  /** Where the LAYER sits inside the frame. Snapshot coordinates are layer-relative, and the layer
   *  is only the document's box — a bottom sheet's is pinned to the bottom, a modal's is centered.
   *  So this offset is what converts between the two spaces, for hit-testing and for outlines. */
  const [origin, setOrigin] = useState({ x: 0, y: 0 });

  /** Re-queried, never cached: switching `surface` destroys the shadow root and builds a new one. */
  const layerEl = (): HTMLElement | null =>
    frameRef.current?.firstElementChild?.shadowRoot?.querySelector('[data-phq-layer]') ?? null;

  const sync = (handle: MountHandle): void => {
    setNodes(handle.snapshot().nodes);
    const frame = frameRef.current;
    const layer = layerEl();
    if (!frame || !layer) return;
    const f = frame.getBoundingClientRect();
    const l = layer.getBoundingClientRect();
    setOrigin({ x: l.left - f.left, y: l.top - f.top });
  };

  const actionRef = useRef(onAction);
  useEffect(() => {
    actionRef.current = onAction;
  });

  // Mount ONCE. Everything after is `update()` — remounting per edit would drop the shadow root,
  // re-register fonts and flash the canvas on every keystroke.
  useEffect(() => {
    const handle = mount(doc, {
      container: frameRef.current as HTMLElement,
      viewport,
      ...(assets ? { assets } : {}),
      onDismiss: () => {},
      // THROUGH A REF, NOT THE CLOSURE. `mount()` runs exactly once and `update()` does not re-pass
      // its callbacks, so capturing `onAction` here would freeze the first render's version of it
      // forever — and it would look like it worked, because the first one is usually right.
      onAction: (action, nodeId) => actionRef.current?.(action, nodeId),
    });
    handleRef.current = handle;
    void handle.ready.then(() => sync(handle));

    return () => {
      handle.destroy();
      handleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once; the effect below updates.
  }, []);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;
    // ponytail: no debounce. `update()` is a full repaint bounded by MAX_LAYOUT_NODES=1024 — a few
    // ms — and React already coalesces to one render per keystroke. Add a rAF only if a profile says so.
    handle.update(doc, { viewport, ...(assets ? { assets } : {}) });
    sync(handle);
  }, [doc, viewport, assets]);

  const at = (e: React.MouseEvent): string | null => {
    const handle = handleRef.current;
    const layer = layerEl();
    if (!handle || !layer) return null;
    // Measured live, not from `origin`: a click can land in the same frame as the edit that moved
    // the layer, and a one-render-stale offset selects the wrong node.
    const box = layer.getBoundingClientRect();
    const id = handle.nodeAt(e.clientX - box.left, e.clientY - box.top);
    return id === null ? null : toDocId(id);
  };

  /** An empty container HAS a frame — it is just a zero-area one, sitting exactly where the
   *  placeholder should go. So the solver still tells us where to draw it. */
  const empties = useMemo(() => {
    const found = emptyContainers(doc.root);
    return nodes
      .filter((node) => found.has(node.id))
      .map((node) => ({ id: node.id, block: found.get(node.id)!, frame: node.frame }));
  }, [doc, nodes]);

  const outline = (id: string | null, className: string): React.JSX.Element | null => {
    const node = id === null ? undefined : nodes.find((n) => n.id === id);
    if (!node) return null;
    const [x, y, w, h] = node.frame;
    return <div className={className} style={{ left: origin.x + x, top: origin.y + y, width: w, height: h }} />;
  };

  return (
    <div
      className="relative shrink-0 shadow-2xl"
      style={{ width: viewport.w, height: viewport.h }}
      onClick={(e) => onSelect(at(e))}
      onMouseMove={(e) => setHover(at(e))}
      onMouseLeave={() => setHover(null)}
    >
      {/* The simulated device screen the overlay sits on. */}
      <div
        ref={frameRef}
        className="size-full overflow-hidden rounded-[28px]"
        style={{
          position: 'relative',
          contain: 'paint',
          background: viewport.color_scheme === 'dark' ? '#09090b' : '#ffffff',
        }}
      />

      {/* Outlines live OUTSIDE the frame: inside it, the overlay host's z-index (2147483000) wins.
          Positioned straight from the solver's rects — never re-derived from the DOM. And never a
          resize handle: size is an inspector property, handles would imply freeform. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[28px]">
        {outlines
          ? nodes.map((node) => (
              <div
                key={node.id}
                className="absolute border border-indigo-400/30"
                style={{
                  left: origin.x + node.frame[0],
                  top: origin.y + node.frame[1],
                  width: node.frame[2],
                  height: node.frame[3],
                }}
              />
            ))
          : null}

        {empties.map(({ id, block, frame }) => (
          <button
            key={id}
            type="button"
            // The one thing on this overlay that IS a click target — because the node it stands for
            // has no area, so the renderer's own hit-test can never find it.
            className="pointer-events-auto absolute flex items-center justify-center rounded border border-dashed border-indigo-400/80 bg-indigo-400/5 text-[10px] text-indigo-500"
            style={{
              left: origin.x + frame[0],
              top: origin.y + frame[1],
              width: Math.max(frame[2], EMPTY_MIN_W),
              height: Math.max(frame[3], EMPTY_MIN_H),
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(id);
            }}
          >
            empty {block}
          </button>
        ))}

        {hover !== null && hover !== selection
          ? outline(hover, 'absolute border border-indigo-400/70')
          : null}
        {outline(selection, 'absolute border-2 border-indigo-500')}
      </div>
    </div>
  );
}
